import {
    ResourceAllocationState,
    Project,
    ProjectPhase
} from '../../../types';
import {removeProjectCascade} from '../cascadeDelete';

export const projectHandlers = {
    ProjectAdded: (
        state: ResourceAllocationState,
        data: Project
    ): ResourceAllocationState => {
        return {
            ...state,
            projects: {...state.projects, [data.id]: data}
        };
    },
    ProjectUpdated: (
        state: ResourceAllocationState,
        data: {id: string; updates: Partial<Project>}
    ): ResourceAllocationState => {
        return {
            ...state,
            projects: {
                ...state.projects,
                [data.id]: {
                    ...state.projects[data.id],
                    ...data.updates
                }
            }
        };
    },
    ProjectDeleted: (
        state: ResourceAllocationState,
        data: {id: string}
    ): ResourceAllocationState => {
        // Mirror the DB cascade: also prune the project's phases, assignments,
        // allocations, project-tags and project-people from local state.
        return removeProjectCascade(state, data.id);
    },
    // Atomic project time shift (resume with re-timing; whole or partial). The
    // server broadcasts to EVERYONE including the initiator — this handler is
    // the single convergence path; no optimistic update exists anywhere.
    // Project and phases are replaced from the payload (authoritative);
    // allocations are windowed client-side, so the ones we hold are shifted by
    // the offset — only those at/after fromWeek on a partial move (rows
    // shifted beyond the loaded window stay in state harmlessly; rows shifted
    // INTO it from outside arrive on the next window load/refetch).
    ProjectShifted: (
        state: ResourceAllocationState,
        data: {
            projectId: string;
            offsetWeeks: number;
            fromWeek?: number | null;
            project: Project;
            phases: ProjectPhase[];
        }
    ): ResourceAllocationState => {
        const phases = {...state.phases};
        for (const phase of data.phases) {
            phases[phase.id] = phase;
        }
        const shifts = (allocation: {
            projectId: string;
            globalWeekNumber: number;
        }) =>
            allocation.projectId === data.projectId &&
            (data.fromWeek == null ||
                allocation.globalWeekNumber >= data.fromWeek);
        const allocations = Object.fromEntries(
            Object.entries(state.allocations).map(([id, allocation]) =>
                shifts(allocation)
                    ? [
                          id,
                          {
                              ...allocation,
                              globalWeekNumber:
                                  allocation.globalWeekNumber +
                                  data.offsetWeeks
                          }
                      ]
                    : [id, allocation]
            )
        );
        return {
            ...state,
            projects: {...state.projects, [data.projectId]: data.project},
            phases,
            allocations
        };
    }
};
